import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Heart, Search, ShoppingBag, UserRound, Menu, X, ArrowRight, MessageCircle, ChevronDown } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { api, imgUrl } from "../api";
import { CATEGORY_TAXONOMY, categoryPath } from "../taxonomy";

const NAV = [
  { to: "/", label: "Home" },
];

export default function Header() {
  const { cartCount } = useCart();
  const { settings, whatsappLink } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [navCategories, setNavCategories] = useState(CATEGORY_TAXONOMY);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 15);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    api.get("/api/categories/tree").then(({ data }) => {
      if (Array.isArray(data) && data.length) setNavCategories(data);
    }).catch(() => {});
  }, []);

  const showAnnouncement = Boolean(settings.announcement_active && settings.announcement_bar);
  const logoPath = settings.logo_url || "/assets/logo.png";

  if (location.pathname.startsWith("/admin")) {
    return null;
  }

  return (
    <>
      <div className={`site-header-wrapper ${scrolled ? "is-scrolled" : ""}`}>
        {showAnnouncement && (
          <div className="announcement-bar" data-testid="announcement-bar">
            <span>{settings.announcement_bar}</span>
            {settings.launch_offer_active && settings.launch_offer_text && (
              <>
                <span className="dot-sep">{"\u00b7"}</span>
                <span className="offer-highlight">{settings.launch_offer_text}</span>
              </>
            )}
          </div>
        )}

        <header className="site-header" data-testid="site-header">
          <div className="header-inner">
            {/* Left: Mobile menu toggle + secondary quick nav */}
            <div className="header-left">
              <button
                className="menu-toggle-btn"
                onClick={() => setOpen(true)}
                data-testid="mobile-menu-button"
                aria-label="Open navigation menu"
              >
                <Menu size={22} />
                <span className="menu-toggle-text">Menu</span>
              </button>
              <Link to="/" className="brand-logo-link" data-testid="brand-home-link">
                <img
                  src={imgUrl(logoPath)}
                  alt={settings.brand_name || "Lalita Soujanya"}
                  className="brand-logo-img"
                  onError={(e) => {
                    // Fallback to text if image fails
                    e.currentTarget.style.display = "none";
                    if (e.currentTarget.nextSibling) {
                      e.currentTarget.nextSibling.style.display = "block";
                    }
                  }}
                />
                <span className="brand-text-fallback" style={{ display: "none" }}>
                  {settings.brand_name || "LALITA SOUJANYA"}
                </span>
              </Link>
            </div>

            {/* Desktop navigation sits beside the house mark. */}
            <nav className="desktop-nav-bar" data-testid="main-navigation">
              <div className="desktop-nav-inner">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) => `desktop-nav-link ${isActive ? "active" : ""}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  >
                    {item.label}
                  </NavLink>
                ))}
                {navCategories.map((category) => (
                  <div className="nav-category-trigger" key={category.slug}>
                    <NavLink to={categoryPath(category)} className="desktop-nav-link nav-category-link">
                      {category.name}<ChevronDown size={12} strokeWidth={1.7} />
                    </NavLink>
                    <div className="nav-category-dropdown">
                      <Link className="nav-category-all" to={categoryPath(category)}>View all {category.name}</Link>
                      {category.children.map((child) => (
                        <Link key={child.slug} to={categoryPath(category, child)}>{child.name}</Link>
                      ))}
                    </div>
                  </div>
                ))}
                <NavLink to="/about" className={({ isActive }) => `desktop-nav-link ${isActive ? "active" : ""}`}>About</NavLink>
              </div>
            </nav>

            {/* Right: Actions */}
            <div className="header-right">
              <button
                className="action-icon-btn"
                onClick={() => navigate("/shop")}
                data-testid="search-link"
                aria-label="Search collection"
                title="Search"
              >
                <Search size={19} />
              </button>
              <Link
                to={user ? "/account" : "/login"}
                className="action-icon-btn"
                data-testid="account-link"
                aria-label="My Account"
                title="Account"
              >
                <UserRound size={19} />
              </Link>
              <Link
                to="/wishlist"
                className="action-icon-btn wishlist-icon"
                data-testid="wishlist-link"
                aria-label="Saved Items"
                title="Wishlist"
              >
                <Heart size={19} />
              </Link>
              <Link
                to="/cart"
                className="action-icon-btn bag-icon"
                data-testid="cart-link"
                aria-label="Shopping Bag"
                title="Bag"
              >
                <ShoppingBag size={19} />
                {cartCount > 0 && (
                  <span className="cart-badge" data-testid="cart-count">
                    {cartCount}
                  </span>
                )}
              </Link>
              {user?.role === "admin" && (
                <Link to="/admin" className="studio-pill-badge" data-testid="nav-admin-link">
                  Studio
                </Link>
              )}
            </div>
          </div>

        </header>
      </div>

      {/* Mobile Drawer Menu */}
      <div className={`mobile-drawer-overlay ${open ? "is-open" : ""}`} onClick={() => setOpen(false)}>
        <div
          className={`mobile-drawer ${open ? "is-open" : ""}`}
          onClick={(e) => e.stopPropagation()}
          data-testid="mobile-menu"
        >
          <div className="mobile-drawer-header">
            <Link to="/" onClick={() => setOpen(false)} className="mobile-drawer-logo">
              <img src={imgUrl(logoPath)} alt={settings.brand_name} />
            </Link>
            <button
              onClick={() => setOpen(false)}
              className="drawer-close-btn"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X size={22} />
            </button>
          </div>

          <div className="mobile-drawer-content">
            <div className="drawer-section-title">Collections</div>
            <nav className="mobile-nav-list">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `mobile-nav-item ${isActive ? "active" : ""}`}
                >
                  <span>{item.label}</span>
                  <ArrowRight size={14} className="nav-arrow" />
                </NavLink>
              ))}
              {navCategories.map((category) => (
                <div className="mobile-taxonomy-group" key={category.slug}>
                  <Link to={categoryPath(category)} onClick={() => setOpen(false)} className="mobile-taxonomy-heading">
                    {category.name}
                  </Link>
                  {category.children.map((child) => (
                    <Link key={child.slug} to={categoryPath(category, child)} onClick={() => setOpen(false)} className="mobile-taxonomy-child">
                      {child.name}
                    </Link>
                  ))}
                </div>
              ))}
              <Link to="/about" onClick={() => setOpen(false)} className="mobile-nav-item">
                <span>About</span><ArrowRight size={14} />
              </Link>
              {user?.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} className="mobile-nav-item studio-item">
                  <span>Studio Admin Panel</span>
                  <ArrowRight size={14} />
                </Link>
              )}
            </nav>

            <div className="mobile-drawer-footer">
              <a
                href={whatsappLink("Hi Lalita Soujanya, I have an enquiry.")}
                target="_blank"
                rel="noreferrer"
                className="mobile-whatsapp-btn"
              >
                <MessageCircle size={16} /> WhatsApp Atelier
              </a>
              <div className="mobile-contact-info">
                <div>{settings.email || "itsme.soujanya.g@gmail.com"}</div>
                <div>{settings.whatsapp_number || "+91 8309550349"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
